import React, { useCallback, useEffect, useState } from "react"
import { Badge, Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap"
import { createSupportContact, deleteSupportContact, getSupportContacts, updateSupportContact } from "helpers/backendHelpers/customerSupport"

const EMPTY_CONTACT = { mobileNo: "", callEnabled: true, whatsappEnabled: true }

// Native checkboxes do not toggle reliably under this theme; the Cheque Print editor uses the same button-based box.
const CheckToggle = ({ label, checked, onToggle }) => (
  <div className="d-flex align-items-center gap-2">
    <button
      type="button"
      onClick={onToggle}
      aria-label={`Toggle ${label}`}
      style={{
        width: 18,
        height: 18,
        padding: 0,
        flex: "none",
        border: "1px solid " + (checked ? "#1976d2" : "#adb5bd"),
        borderRadius: 3,
        background: checked ? "#1976d2" : "#fff",
        color: "#fff",
        fontSize: 12,
        lineHeight: "16px",
        cursor: "pointer",
      }}
    >
      {checked ? "✓" : ""}
    </button>
    <span style={{ cursor: "pointer" }} onClick={onToggle}>{label}</span>
  </div>
)

const CustomerSupportCard = () => {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const response = await getSupportContacts()
      setContacts(response?.payload || [])
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const setField = (field, value) => setEditing(prev => ({ ...prev, [field]: value }))
  const canSave = editing && /^\d{10}$/.test(editing.mobileNo) && (editing.callEnabled || editing.whatsappEnabled)

  const save = async () => {
    setSaving(true)
    try {
      const data = { mobileNo: editing.mobileNo, callEnabled: editing.callEnabled, whatsappEnabled: editing.whatsappEnabled }
      if (editing._id) await updateSupportContact(editing._id, data)
      else await createSupportContact(data)
      setEditing(null)
      await load()
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setSaving(false)
    }
  }

  const remove = async contact => {
    if (!window.confirm(`Delete support number ${contact.mobileNo}?`)) return
    try {
      await deleteSupportContact(contact._id)
      await load()
    } catch (_) {
      // api_helper already shows the error toast
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Label className="fw-bold mb-0 d-block">Customer Support</Label>
            <small className="text-muted">These numbers appear in the web app header under the Customer Support icon.</small>
          </div>
          <Button color="primary" type="button" onClick={() => setEditing({ ...EMPTY_CONTACT })}>
            <i className="bx bx-plus me-1"></i>Add Number
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : contacts.length ? (
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead>
                <tr><th>#</th><th>Mobile No.</th><th>Options</th><th className="text-end">Actions</th></tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={contact._id}>
                    <td>{index + 1}</td>
                    <td>{contact.mobileNo}</td>
                    <td>
                      {contact.callEnabled && <Badge color="primary" className="me-1">Call</Badge>}
                      {contact.whatsappEnabled && <Badge color="success">WhatsApp</Badge>}
                    </td>
                    <td className="text-end">
                      <Button color="primary" outline size="sm" className="me-2" type="button" onClick={() => setEditing({ ...contact })}>
                        <i className="bx bx-edit-alt me-1"></i>Edit
                      </Button>
                      <Button color="danger" outline size="sm" type="button" onClick={() => remove(contact)}>
                        <i className="bx bx-trash me-1"></i>Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted">No support numbers added yet.</div>
        )}
      </CardBody>

      <Modal isOpen={!!editing} toggle={() => setEditing(null)} centered>
        <ModalHeader toggle={() => setEditing(null)}>{editing?._id ? "Edit Support Number" : "Add Support Number"}</ModalHeader>
        {editing && (
          <ModalBody>
            <div className="mb-3">
              <Label>Mobile No.</Label>
              <Input value={editing.mobileNo} maxLength={10} inputMode="numeric" placeholder="10 digit mobile number" onChange={e => setField("mobileNo", e.target.value.replace(/\D/g, "").slice(0, 10))} />
            </div>
            <Row>
              <Col xs={6}>
                <CheckToggle label="Call" checked={editing.callEnabled} onToggle={() => setEditing(prev => ({ ...prev, callEnabled: !prev.callEnabled }))} />
              </Col>
              <Col xs={6}>
                <CheckToggle label="WhatsApp" checked={editing.whatsappEnabled} onToggle={() => setEditing(prev => ({ ...prev, whatsappEnabled: !prev.whatsappEnabled }))} />
              </Col>
            </Row>
            {!editing.callEnabled && !editing.whatsappEnabled && <small className="text-danger">Select at least one option.</small>}
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" type="button" onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" type="button" disabled={saving || !canSave} onClick={save}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  )
}

export default CustomerSupportCard
